import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Box } from "@mui/material";
import { withRouter } from "../../../providers/withRouter";
import { UsuarioContext } from "../../../providers/userContext";
import { DataTable } from "../../../components";
import BreadCrumb from "../../../components/navbar/Breadcrumb";
import { IoSave, IoDocumentText } from "react-icons/io5";
import { getAAFFs } from "../../../services";
import FiltrosAAFFInformes from "../../../components/filtros/filtrosAAFFInformes";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useRecogerURLParamsFiltros,
  construirURLParamsFiltros,
} from "./../../../functions/parametrosURL/funcionesParamsURL";

const AAFFInformes = () => {
  const { t } = useTranslation(['aaff', 'common']);

  const comunidadesRows = [
    {
      id: "aff_name",
      numeric: false,
      disablePadding: false,
      label: t('reports.columns.admin'),
      overrideFunc: (data, row) => (
        <Link to={"/aaff/" + row.nif + "/comunidades"}>{data}</Link>
      ),
    },
    {
      id: "nif",
      numeric: false,
      disablePadding: false,
      label: t('common:form.nif').toUpperCase(),
      overrideFunc: (data, row) => row.parent_id ? "" : data,
    },
    {
      id: "informeComunidades",
      numeric: false,
      disablePadding: false,
      label: t('reports.columns.communitiesReport'),
      overrideFunc: (row) => (
        <Link
          to={`/doc-render/${row}/situacion-comunidades`}
          target={"_blank"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "120px",
            height: "40px",
            textDecoration: "none",
          }}
        >
          <div className="bigBtn">
            <IoDocumentText alt={t('reports.columns.communitiesReport')} />
            <span style={{ marginLeft: 15 }}>{t('reports.generate')}</span>
          </div>
        </Link>
      ),
    },
    {
      id: "informeComunidadesSinProveedores",
      numeric: false,
      disablePadding: false,
      label: t('reports.columns.communitiesWithoutSuppliers'),
      noObjeto: true,
      overrideFunc: (row) => (
        <Link
          to={`/doc-render/${row}/comunidades-sin-proveedores`}
          target={"_blank"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "120px",
            height: "40px",
            textDecoration: "none",
          }}
        >
          <div className="bigBtn">
            <IoDocumentText alt={t('reports.columns.communitiesWithoutSuppliers')} />
            <span style={{ marginLeft: 15 }}>{t('reports.generate')}</span>
          </div>
        </Link>
      ),
    },
    {
      id: "informeProveedores",
      numeric: false,
      disablePadding: false,
      label: t('reports.columns.suppliersReport'),
      overrideFunc: (row) => (
        <Link
          to={`/doc-render/${row}/situacion-proveedores`}
          target={"_blank"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "120px",
            height: "40px",
            textDecoration: "none",
          }}
        >
          <div className="bigBtn">
            <IoDocumentText alt={t('reports.columns.suppliersReport')} />
            <span style={{ marginLeft: 15 }}>{t('reports.generate')}</span>
          </div>
        </Link>
      ),
    },
    {
      id: "informeRiesgos",
      numeric: false,
      disablePadding: false,
      label: t('reports.columns.risksReport'),
      overrideFunc: (row) => (
        <Link
          to={`/doc-render/${row}/situacion-riesgos`}
          target={"_blank"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "120px",
            height: "40px",
            textDecoration: "none",
          }}
        >
          <div className="bigBtn">
            <IoDocumentText alt={t('reports.columns.risksReport')} />
            <span style={{ marginLeft: 15 }}>{t('reports.generate')}</span>
          </div>
        </Link>
      ),
    },
  ];
  const user = useContext(UsuarioContext);
  const { filtrosIniciales, filtroOrder } = useRecogerURLParamsFiltros();
  const [busqueda, setBusqueda] = useState(filtrosIniciales);
  const [recargar, setRecargar] = useState(0);
  const [busquedaActiva, setBusquedaActiva] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clearFilterActivo, setClearFilterActivo] = useState(false);
  const [data, setData] = useState({
    meta: {},
    rows: [],
  });

  const refControladorAborto = useRef();

  useEffect(() => {
    setRecargar(false);
  }, [recargar]);

  const navigate = useNavigate();

  const clearFilters = () => {
    setBusqueda({});
    setClearFilterActivo(true);
  };

  const fetchData = async ({
    offset,
    limit,
    filtros = busqueda,
    order,
    orderBy,
  } = {}) => {

    if (refControladorAborto.current) {
      refControladorAborto.current.abort();
    }
    const controller = new AbortController();
    refControladorAborto.current = controller;

    let usuario = { commer_id: user.id, informe: true };

    orderBy = orderBy || filtroOrder.ordenadoPor || "id";
    order = order || filtroOrder.orden || "ASC";

    construirURLParamsFiltros(
      navigate,
      offset || 1,
      limit || 50,
      filtros,
      order,
      orderBy
    );

    const payload = {
      filtros: { ...filtros, ...usuario },
      params: { page: offset, limit, order, orderBy },
    };

    try {
      setLoading(true);
      const response = await getAAFFs(payload, { signal: controller.signal });
      if (!controller.signal.aborted) {
        const { meta, data } = response;
        setData({
          meta,
          rows: formatData(data),
        });
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
      } else {
        console.log(error);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const formatData = (data) => {
    return data.map((row) => ({
      aff_name: row.razonSocial,
      nif: row.nif,
      parent_id: row.parent_id,
      informeComunidades: row.nif,
      informeProveedores: row.nif,
      informeComunidadesSinProveedores: row.nif,
      informeRiesgos: row.nif,
    }));
  };

  const filtrarDatos = (filtros) => {
    setBusqueda(filtros);
    setBusquedaActiva(true);
    // fetchData({ offset: 1, limit: limite, filtros: filtros });
  };

  useEffect(() => {
      return () => {
        if (refControladorAborto.current) {
          refControladorAborto.current.abort();
        }
      };
    }, []);

  return (
    <>
      <BreadCrumb
        rutas={[{ path: "/aaff-informes", label: t('title') }]}
        style={{ margin: "0 10px" }}
      />
      <div className="pageContent tableContainer">
        <FiltrosAAFFInformes
          onFilter={filtrarDatos}
          clearFilter={clearFilters}
          filtrosIniciales={busqueda}
        />
        <div className="tableWrapper">
          <Box className="pageContent">
            <Box sx={{ mb: 10 }}>
              <DataTable
                data={data.rows}
                isLoading={loading}
                headers={comunidadesRows}
                fetcher={fetchData}
                currentPage={data.current_page}
                meta={data.meta}
                totalItems={data.total}
                totalPages={data.totalPages}
                nextPage={data.next_page}
                filtros={busqueda}
                busquedaActiva={busquedaActiva}
                setBusquedaActiva={setBusquedaActiva}
                clearFilterActivo={clearFilterActivo}
                setClearFilterActivo={setClearFilterActivo}
                mensaje={t('messages.noAaff')}
              />
            </Box>
          </Box>
        </div>
      </div>
    </>
  );
};

export default withRouter(AAFFInformes);
